import React from 'react';
import { Scan, Check, Radar } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ScreenshotCard from './ScreenshotCard';

const screenshots = [
  { key: 'overview', file: 'overview.webp', wide: true },
  { key: 'hosts', file: 'hosts.webp' },
  { key: 'findingDetail', file: 'finding-detail.webp' },
  { key: 'scanners', file: 'scanners.webp' },
  { key: 'lookalikeDomains', file: 'lookalike-domains.webp' },
  { key: 'discoveredHosts', file: 'discovered-hosts.webp' },
  { key: 'darknet', file: 'darknet.webp' }
];

const ExternalScan = () => {
  const { t } = useLanguage();

  return (
    <section id="external-scan" className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-cyan-600/5 rounded-full blur-3xl" />
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 p-4 flex items-center justify-center">
              <Scan className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            {t.externalScan.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-4xl mx-auto">
            {t.externalScan.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {t.externalScan.features.map((feature, index) => (
            <div
              key={index}
              className="relative group"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300" />
              <div className="relative bg-gradient-to-br from-cyan-950/30 to-blue-950/20 border border-cyan-500/30 rounded-lg p-6 flex items-start gap-4 h-full">
                <Check className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <p className="text-gray-200 text-lg">{feature}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Scanners */}
        <div className="relative mb-16">
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg blur opacity-20" />
          <div className="relative bg-gradient-to-br from-cyan-950/40 to-blue-950/30 border border-cyan-500/30 rounded-lg p-8">
            <div className="flex items-center gap-4 mb-3">
              <Radar className="w-8 h-8 text-cyan-400 flex-shrink-0" />
              <h3 className="text-2xl font-bold text-white">{t.externalScan.scannersTitle}</h3>
            </div>
            <p className="text-gray-400 mb-6">{t.externalScan.scannersSubtitle}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {t.externalScan.scanners.map((scanner, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-black/30 border border-cyan-500/20 rounded-lg p-4"
                >
                  <span className="text-cyan-400 font-bold font-mono">{String(index + 1).padStart(2, '0')}</span>
                  <span className="text-gray-200">{scanner}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Screenshots */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {screenshots.map(({ key, file, wide }) => (
            <ScreenshotCard
              key={key}
              file={file}
              wide={wide}
              title={t.externalScan.screenshots[key].title}
              description={t.externalScan.screenshots[key].description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExternalScan;
